import { AnimatePresence } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import EnterViewportAnimation from "../EnterViewportAnimation";
import ImageOrVideo from "../ImageOrVideo";
import SimpleOverlay from "../modal/SimpleOverlay";
import Carousel from "../Carousel";
import classnames from "classnames";
import "./style.scss";
import { unwrapAsset } from "../../utils/CockpitUtils";
import ProductLink from "../ProductLink";
import COLORS from "../../config/colors";

const ImageCollage = ({
  images = [],
  layout = "auto",
  crop_images = false,
  caption,
  byline,
  product_link,
  className,
  theme
}) => {
  const isSmall = useMediaQuery({ maxWidth: 575.98 });

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const sliderRef = useRef();

  useEffect(() => {
    if (images) {
      setSelectedIndex(images.length > 1 ? 1 : 0);
    }
  }, [images]);

  const items = images
    ?.map(item => unwrapAsset(item.value))
    .map(image => {
      return {
        ...image,
        aspectRatio: image.height ? image.width / image.height : 1
      };
    })
    .sort((a, b) => a.aspectRatio - b.aspectRatio); //portrait oriented images sorted first

  if (!items) return null;

  if (layout === "auto") {
    layout =
      items.length === 1
        ? "1x1"
        : items.length === 2
        ? "2x1"
        : items.length === 3
        ? "1plus2"
        : "2x2";
  }

  let positionStyles = [];
  let maxImages = 4;
  let midX = 0;
  let gap = 0.8;
  let margin = 2.5;
  if (isSmall) {
    crop_images = true;
    gap = 0.25;
    margin = 1.25;
  }

  switch (layout) {
    case "1x1":
      positionStyles = [
        {
          top: "calc(50 * var(--cvh))",
          left: "50%",
          transform: "translateX(-50%) translateY(-50%)",
          maxWidth: "calc(100% - " + margin * 2 + "rem)",
          width: crop_images
            ? "calc(100% - " + margin * 2 + "rem)"
            : "calc(calc(100 * var(--cvh) - " +
              margin * 2 +
              "rem) * " +
              items[0].aspectRatio +
              ")",
          [crop_images ? "height" : "maxHeight"]:
            "calc(100 * var(--cvh) - " + margin * 2 + "rem)"
        }
      ];
      maxImages = 1;
      break;
    case "2x1":
      midX =
        25 +
        50 *
          (items[0].aspectRatio /
            (items[0].aspectRatio + items[1].aspectRatio));
      positionStyles = [
        {
          top: "calc(50 * var(--cvh))",
          transform: "translateY(-50%)",
          right: "calc(" + (100 - midX) + "% + " + gap / 2 + "rem)",
          [crop_images ? "width" : "maxWidth"]:
            "calc(" + midX + "% - " + margin + "rem)",
          [crop_images ? "height" : "maxHeight"]:
            "calc(100 * var(--cvh) - " + margin * 2 + "rem)",
          ...(!crop_images
            ? { [items[0].aspectRatio < 1 ? "height" : "width"]: "100%" }
            : {})
        },
        {
          top: "calc(50 * var(--cvh))",
          transform: "translateY(-50%)",
          left: "calc(" + midX + "% + " + gap / 2 + "rem)",
          [crop_images ? "width" : "maxWidth"]:
            "calc(" + (100 - midX) + "% - " + margin + "rem)",
          [crop_images ? "height" : "maxHeight"]:
            "calc(100 * var(--cvh) - " + margin * 2 + "rem)",
          ...(!crop_images
            ? { [items[1].aspectRatio < 1 ? "height" : "width"]: "100%" }
            : {})
        }
      ];
      maxImages = 2;
      break;
    case "1plus2":
      positionStyles = [
        {
          top: "calc(50 * var(--cvh))",
          transform: "translateY(-50%)",
          right: "calc(58% + " + gap / 2 + "rem)",
          [crop_images ? "width" : "maxWidth"]: "calc(42% - " + margin + "rem)",
          [crop_images ? "height" : "maxHeight"]: crop_images
            ? "calc(100 * var(--cvh) - " + margin * 2 + "rem)"
            : "calc(80 * var(--cvh) - " + margin * 2 + "rem)",
          ...(!crop_images
            ? { [items[0].aspectRatio < 0.8 ? "height" : "width"]: "100%" }
            : {})
        },
        {
          bottom: "calc(50 * var(--cvh) + " + gap / 2 + "rem)",
          left: "calc(42% + " + gap / 2 + "rem)",
          [crop_images ? "width" : "maxWidth"]: "calc(58% - " + margin + "rem)",
          height: "calc(50 * var(--cvh) - " + (margin + gap / 2) + "rem)"
        },
        {
          top: "calc(50 * var(--cvh) + " + gap / 2 + "rem)",
          left: "calc(42% + " + gap / 2 + "rem)",
          [crop_images ? "width" : "maxWidth"]: "calc(58% - " + margin + "rem)",
          height: "calc(50 * var(--cvh) - " + (margin + gap / 2) + "rem)"
        }
      ];
      maxImages = 3;
      break;
    case "2plus1":
      positionStyles = [
        {
          top: "calc(50 * var(--cvh))",
          transform: "translateY(-50%)",
          left: "calc(58% + " + gap / 2 + "rem)",
          [crop_images ? "width" : "maxWidth"]: "calc(42% - " + margin + "rem)",
          [crop_images ? "height" : "maxHeight"]: crop_images
            ? "calc(100 * var(--cvh) - " + margin * 2 + "rem)"
            : "calc(80 * var(--cvh) - " + margin * 2 + "rem)",
          ...(!crop_images
            ? { [items[0].aspectRatio < 0.8 ? "height" : "width"]: "100%" }
            : {})
        },
        {
          bottom: "calc(50 * var(--cvh) + " + gap / 2 + "rem)",
          right: "calc(42% + " + gap / 2 + "rem)",
          [crop_images ? "width" : "maxWidth"]: "calc(58% - " + margin + "rem)",
          height: "calc(50 * var(--cvh) - " + (margin + gap / 2) + "rem)"
        },
        {
          top: "calc(50 * var(--cvh) + " + gap / 2 + "rem)",
          right: "calc(42% + " + gap / 2 + "rem)",
          [crop_images ? "width" : "maxWidth"]: "calc(58% - " + margin + "rem)",
          height: "calc(50 * var(--cvh) - " + (margin + gap / 2) + "rem)"
        }
      ];
      maxImages = 3;
      break;
    case "2x2":
      midX =
        25 +
        50 *
          (items[0].aspectRatio /
            (items[0].aspectRatio + items[3].aspectRatio));
      positionStyles = [
        {
          bottom: "calc(50 * var(--cvh) + " + gap / 2 + "rem)",
          right: "calc(" + (100 - midX) + "% + " + gap / 2 + "rem)",
          [crop_images ? "width" : "maxWidth"]:
            "calc(" + midX + "% - " + margin + "rem)",
          maxHeight: "calc(50 * var(--cvh) - " + (margin + gap / 2) + "rem)",
          height: "100%"
        },
        {
          top: "calc(50 * var(--cvh) + " + gap / 2 + "rem)",
          left: "calc(" + (100 - midX) + "% + " + gap / 2 + "rem)",
          [crop_images ? "width" : "maxWidth"]:
            "calc(" + midX + "% - " + margin + "rem)",
          maxHeight: "calc(50 * var(--cvh) - " + (margin + gap / 2) + "rem)",
          height: "100%"
        },
        {
          bottom: "calc(50 * var(--cvh) + " + gap / 2 + "rem)",
          left: "calc(" + midX + "% + " + gap / 2 + "rem)",
          [crop_images ? "width" : "maxWidth"]:
            "calc(" + (100 - midX) + "% - " + margin + "rem)",
          maxHeight: "calc(50 * var(--cvh) - " + (margin + gap / 2) + "rem)",
          height: "100%"
        },
        {
          top: "calc(50 * var(--cvh) + " + gap / 2 + "rem)",
          right: "calc(" + midX + "% + " + gap / 2 + "rem)",
          [crop_images ? "width" : "maxWidth"]:
            "calc(" + (100 - midX) + "% - " + margin + "rem)",
          maxHeight: "calc(50 * var(--cvh) - " + (margin + gap / 2) + "rem)",
          height: "100%"
        }
      ];
      maxImages = 4;
      break;
  }

  const classes = classnames(
    "image-collage",
    "image-collage-layout-" + layout,
    className,
    "theme-" + theme
  );

  return (
    <div className={classes}>
      <AnimatePresence>
        {modalIsOpen && (
          <SimpleOverlay animateScale close={() => setModalIsOpen(false)}>
            <Carousel
              ref={sliderRef}
              className="gallery-carousel"
              direction="horizontal"
              showMarkers
              smallMarkers
              centeredButtons
              slidesData={items.slice(0, maxImages)}
              renderSlide={(image, idx) => (
                <div className={"carousel-slide"} key={idx}>
                  <ImageOrVideo
                    key={idx}
                    image={image}
                    containImage
                    centerImage
                    className="slide-content"
                  />
                </div>
              )}
              startOverAtEnd
              startIndex={selectedIndex}
            />
          </SimpleOverlay>
        )}
      </AnimatePresence>
      {items.length > 0 && (
        <div className={"collage-images"}>
          {items.map((image, idx) => {
            return (
              <div
                className={classnames(
                  "collage-image",
                  idx === selectedIndex && "selected"
                )}
                style={{
                  aspectRatio: crop_images
                    ? undefined
                    : image.width + "/" + image.height,
                  ...positionStyles[idx],
                  background: image.colors?.[0] || COLORS.bjelin_light_grey
                }}
                onClick={e => {
                  setSelectedIndex(idx);
                  setModalIsOpen(true);
                  e.preventDefault();
                }}
                key={idx}
              >
                <ImageOrVideo
                  image={image}
                  containImage={!crop_images}
                  centerImage
                />
              </div>
            );
          })}
        </div>
      )}
      {(byline || caption || product_link?._id) && (
        <EnterViewportAnimation>
          <div className={"caption-byline-row"}>
            {caption && <span className={"caption"}>{caption}</span>}
            {product_link?._id && (
              <span className="product_link">
                <ProductLink
                  showPrefix
                  id={product_link._id}
                  style={{
                    fontWeight: "500"
                  }}
                />
              </span>
            )}
            {byline && <span className={"byline"}>{byline}</span>}
          </div>
        </EnterViewportAnimation>
      )}
    </div>
  );
};

export default ImageCollage;
